import React, { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Play, ArrowRight } from "lucide-react";
import Button from "../common/Button";
import heroVideo from "../../assets/videos/Iravatham-hero.mp4";

gsap.registerPlugin(ScrollTrigger);

export default function Hero() {
  const heroRef = useRef(null);
  const textRef = useRef(null);
  const videoRef = useRef(null);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);

  const textOverlays = [
    {
      start: 0,
      end: 8,
      title: "From Shell to Sanctuary",
    },
    {
      start: 8,
      end: 24,
      title: "We understand what home means to you",
    },
    {
      start: 24,
      end: Infinity,
      title: "Timeless Spaces. Modern Living",
    },
  ];

  // Handle video time updates
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      const currentTime = video.currentTime;
      const newIndex = textOverlays.findIndex(
        (overlay) => currentTime >= overlay.start && currentTime < overlay.end,
      );
      if (newIndex !== -1 && newIndex !== currentTextIndex) {
        setCurrentTextIndex(newIndex);
      }
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    return () => video.removeEventListener("timeupdate", handleTimeUpdate);
  }, [currentTextIndex]);

  useEffect(() => {
    if (!heroRef.current) return;

    const element = heroRef.current;
    let ctx = null;

    try {
      ctx = gsap.context(() => {
        // Text animations - use fromTo to ensure end state is always visible
        gsap.fromTo(
          ".hero-label",
          { y: 30, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.8, delay: 0.2 },
        );

        gsap.fromTo(
          ".hero-title",
          { y: 50, opacity: 0 },
          { y: 0, opacity: 1, duration: 1, delay: 0.4 },
        );

        gsap.fromTo(
          ".hero-subtitle",
          { y: 30, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.8, delay: 0.6 },
        );

        gsap.fromTo(
          ".hero-cta",
          { y: 30, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.8, delay: 0.8, stagger: 0.1 },
        );
      }, element);
    } catch (error) {
      console.warn("GSAP animation setup error:", error);
    }

    return () => {
      if (ctx) {
        try {
          // Kill ALL ScrollTriggers immediately to unpin any pinned elements
          // This must be synchronous to prevent React from trying to remove moved nodes
          const allTriggers = ScrollTrigger.getAll();
          allTriggers.forEach((trigger) => {
            try {
              trigger.kill();
            } catch (e) {
              // Ignore individual trigger kill errors
            }
          });
          ctx.revert();
        } catch (error) {
          // Silently ignore cleanup errors - React may have already unmounted
          console.warn("GSAP cleanup error (safe to ignore):", error);
        }
      }
    };
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-end justify-center overflow-hidden"
    >
      {/* Background Video */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10" />
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover"
        >
          <source src={heroVideo} type="video/mp4" />
          {/* Fallback image if video doesn't load */}
          <img
            src="https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=1920&q=80"
            alt="Modern interior"
            className="w-full h-full object-cover"
          />
        </video>
      </div>

      {/* Content - Centered Bottom */}
      <div className="relative z-20 w-full pb-16 md:pb-20">
        <div className="container-custom section-padding">
          <div className="flex flex-col items-center text-center max-w-4xl mx-auto">
            <h1
              key={`title-${currentTextIndex}`}
              className="hero-title font-heading text-xl md:text-2xl lg:text-3xl leading-relaxed mb-6 text-white opacity-100 transition-all duration-500 italic whitespace-nowrap"
              style={{ textShadow: "2px 2px 12px rgba(0, 0, 0, 0.8)" }}
            >
              {textOverlays[currentTextIndex].title}
            </h1>

            <div className="relative z-30">
              <Button
                href={`https://wa.me/${
                  process.env.REACT_APP_WHATSAPP_NUMBER || "917995659797"
                }?text=Hi, I want to discuss my interior project with a designer.`}
                variant="outline"
                size="default"
                className="hero-cta bg-brand-accent text-white hover:bg-brand-accent/90 shadow-2xl border-2 border-brand-accent px-6 py-3 text-sm font-semibold"
              >
                Book Consultation
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
